/**
 * Flatpickr 래퍼 – 날짜/시간 입력에 Flatpickr 적용.
 * 컨트롤러에서는 new DatePicker(selector, options).init() 만 사용.
 */
declare global {
  interface Window {
    flatpickr?: FlatpickrFn & { l10ns?: Record<string, unknown> };
  }
}

interface FlatpickrFn {
  (el: HTMLElement, options?: FlatpickrOptions): FlatpickrInstance;
}

interface FlatpickrOptions {
  enableTime?: boolean;
  noCalendar?: boolean;
  dateFormat?: string;
  time_24hr?: boolean;
  locale?: string;
  allowInput?: boolean;
  mode?: "single" | "range" | "multiple";
  defaultDate?: string | string[];
  onChange?: (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => void;
  [key: string]: unknown;
}

interface FlatpickrInstance {
  destroy: () => void;
  setDate?: (date: string | string[] | Date | Date[], triggerChange?: boolean) => void;
}

export interface DatePickerOptions {
  enableTime?: boolean;
  noCalendar?: boolean;
  dateFormat?: string;
  time24hr?: boolean;
  locale?: string;
  allowInput?: boolean;
  /** "range" 면 시작~종료 범위 선택. 미지정 시 단일. */
  mode?: "single" | "range" | "multiple";
  /** 초기 선택값 (편집 시 복원용). range 면 [start, end]. */
  defaultDate?: string | string[];
  /** 선택 변경 콜백 — daterange 의 hidden start/end 동기화 등에 사용. */
  onChange?: (selectedDates: Date[], dateStr: string) => void;
}

export class DatePicker {
  private readonly target: string | HTMLElement;
  private readonly options: DatePickerOptions;
  private instance: FlatpickrInstance | null = null;

  constructor(target: string | HTMLElement, options: DatePickerOptions = {}) {
    this.target = target;
    this.options = options;
  }

  init(): FlatpickrInstance | null {
    const fp = typeof window !== "undefined" ? window.flatpickr : undefined;
    if (typeof fp !== "function") return null;

    const el =
      typeof this.target === "string"
        ? document.querySelector<HTMLElement>(this.target)
        : this.target;
    if (!el) return null;

    const locale =
      this.options.locale ??
      (window.flatpickr?.l10ns?.ko ? "ko" : undefined);

    const opts: FlatpickrOptions = {
      enableTime: this.options.enableTime,
      noCalendar: this.options.noCalendar,
      dateFormat: this.options.dateFormat ?? "Y-m-d",
      time_24hr: this.options.time24hr ?? true,
      locale,
      allowInput: this.options.allowInput ?? true,
      mode: this.options.mode ?? "single",
    };
    if (this.options.defaultDate !== undefined) {
      opts.defaultDate = this.options.defaultDate;
    }
    if (this.options.onChange) {
      const cb = this.options.onChange;
      opts.onChange = (selectedDates, dateStr) => cb(selectedDates, dateStr);
    }

    this.instance = fp(el, opts);
    return this.instance;
  }

  getInstance(): FlatpickrInstance | null {
    return this.instance;
  }

  destroy(): void {
    if (this.instance?.destroy) {
      this.instance.destroy();
      this.instance = null;
    }
  }
}
